/**
 * Development Environment Configuration Entry Point
 * 
 * This file ensures only dev environment config is bundled
 * when building for development deployment.
 */

import type { Environment, EnvironmentConfig } from './environments';
import {
  PRICING_CONFIG,
  PREPAID_PLAN_IDS,
  RECURRING_PLAN_IDS,
  FREE_PLAN_IDS,
  ALL_PLAN_IDS,
  STRIPE_PRICE_TYPES,
  getFreePlan,
  getRecurringPlan,
  getPrepaidPlan,
  getAllRecurringPlans,
  getAllPrepaidPlans,
  getRecommendedRecurringPlan,
  getRecommendedPrepaidPlan,
  formatPrice,
  getPrepaidDurationInSeconds,
  stripeToPrepaidPlan,
} from './pricing';
import {
  getFeaturesForTier,
  getFeaturesForTierAndEnvironment,
  isFeatureEnabled,
  getStorageLimit,
  getDynamicFeatures,
  isDynamicFeatureEnabled,
  RATE_LIMITS,
} from './features';
import {
  getInfrastructureConfig,
  getAWSConfig,
  getDomainConfig,
  getStackName,
  getS3BucketName,
  getDynamoTableName,
  getLambdaFunctionName,
  getSecretName,
} from './infrastructure';
import {
  getSecurityConfig,
  getCryptoConfig,
  getJWTConfig,
  getPasswordConfig,
  getSessionConfig,
  getSecurityHeaders,
  validatePassword,
  getSessionTimeout,
  SECURITY_CONSTANTS,
} from './security';
import {
  PAYMENT_CONFIG,
  getDisplayPaymentMethods,
  getEnabledPaymentMethods,
  getAllPaymentMethods,
  getPaymentMethod,
  isPaymentMethodEnabled,
  getComingSoonPaymentMethods,
} from './payments';
import {
  getNotificationConfig,
  getSlackConfig,
  isSlackEnabled,
  SLACK_EVENT_TYPES,
  SLACK_MESSAGE_COLORS,
} from './notifications';

// Only include dev environment configuration
const ENVIRONMENT: Environment = 'dev';

const environmentConfig: EnvironmentConfig = {
  environment: 'dev',
  displayName: 'Safermark (Dev)',
  apiUrl: 'https://api.safermark.dev.lbsoft.co.nz/api',
  appUrl: 'https://safermark.dev.lbsoft.co.nz',
  extensionDownloadUrl: 'https://chromewebstore.google.com/detail/safermark-secure-private/hdfokkmjfonpciemfincmpnaghdilaff',
  notificationEmail: 'thi.senaratne@gmail.com',
  contactEmail: 'admin@bitdirection.com',
  stripePublishableKey: 'pk_test_51RvsQiRqF1hEzgEyLqcAzvALLQhV8KX6AeuV1UEe051h2R0gRYgVviiEw46OZqv2Pb2nu3xAusaVqeoZeyYKJ9NO004dNUOvhA',
  
  api: {
    corsOrigins: [
      'https://safermark.dev.lbsoft.co.nz',
      'https://www.safermark.dev.lbsoft.co.nz',
      'chrome-extension://dev-extension-id'
    ],
    s3BucketName: 'safermark-vaults-dev-563687506272',
    awsRegion: 'us-east-1',
    dynamoTableName: 'safermark-users-v2-dev'
  },
  
  extension: {
    extensionId: 'dev-extension-id',
    allowDevTools: true,
    showEnvironmentIndicator: true
  },

  analytics: {
    googleAnalyticsId: 'G-NLN3PC3X5B', // Replace with actual dev GA ID
    enabled: true
  },

  development: {
    enableDebugLogs: true,
    enableHotReload: false,
    mockStripePayments: false
  }
};

/**
 * Get current environment (always returns 'dev' for this build)
 */
export function getCurrentEnvironment(): Environment {
  return ENVIRONMENT;
}

/**
 * Get configuration for current environment
 */
export function getEnvironmentConfig(): EnvironmentConfig {
  return environmentConfig;
}

/**
 * Check if current environment is development-like
 */
export function isDevelopmentEnvironment(): boolean {
  return true;
}

/**
 * Check if current environment is production
 */
export function isProductionEnvironment(): boolean {
  return false;
}

/**
 * Legacy compatibility - map old extension environment names to new ones
 */
export function mapLegacyEnvironment(legacyEnv: 'localhost' | 'development' | 'production'): Environment {
  const mapping = {
    'localhost': 'local' as const,
    'development': 'dev' as const,
    'production': 'prod' as const
  };
  return mapping[legacyEnv];
}

/**
 * Main configuration object providing easy access to all configs
 */
export const config = {
  // Current environment detection
  get env() {
    return ENVIRONMENT;
  },
  
  get isDev() {
    return true;
  },
  
  get isProd() {
    return false;
  },
  
  // Environment configuration
  get current() {
    return environmentConfig;
  },
  
  get environment() {
    return environmentConfig;
  },
  
  // Quick access to common values
  get apiUrl() {
    return environmentConfig.apiUrl;
  },
  
  get appUrl() {
    return environmentConfig.appUrl;
  },
  
  get displayName() {
    return environmentConfig.displayName;
  },
  
  get stripePublishableKey() {
    return environmentConfig.stripePublishableKey;
  },
  
  get corsOrigins() {
    return environmentConfig.api.corsOrigins;
  },
  
  get api() {
    return environmentConfig.api;
  },
  
  // AWS configuration shortcuts
  get aws() {
    return getAWSConfig(ENVIRONMENT);
  },
  
  get s3BucketName() {
    return getS3BucketName(ENVIRONMENT);
  },
  
  get dynamoTableName() {
    return getDynamoTableName(ENVIRONMENT);
  },
  
  get secretName() {
    return getSecretName(ENVIRONMENT);
  },
  
  // Security configuration shortcuts
  get security() {
    return getSecurityConfig(ENVIRONMENT);
  },
  
  get crypto() {
    return getCryptoConfig(ENVIRONMENT);
  },
  
  get jwt() {
    return getJWTConfig(ENVIRONMENT);
  },
  
  // Pricing shortcuts
  get pricing() {
    return PRICING_CONFIG;
  },
  
  // Rate limits
  get rateLimits() {
    return RATE_LIMITS;
  },
  
  // Payment methods
  get payments() {
    return PAYMENT_CONFIG;
  },
  
  get paymentMethods() {
    return getDisplayPaymentMethods();
  },
  
  // Security constants
  get securityConstants() {
    return SECURITY_CONSTANTS;
  },
};

// Export all the helper functions and constants
export {
  // Types
  type Environment,
  type EnvironmentConfig,
  
  // Pricing
  PRICING_CONFIG,
  PREPAID_PLAN_IDS,
  RECURRING_PLAN_IDS,
  FREE_PLAN_IDS,
  ALL_PLAN_IDS,
  STRIPE_PRICE_TYPES,
  getFreePlan,
  getRecurringPlan,
  getPrepaidPlan,
  getAllRecurringPlans,
  getAllPrepaidPlans,
  getRecommendedRecurringPlan,
  getRecommendedPrepaidPlan,
  formatPrice,
  getPrepaidDurationInSeconds,
  stripeToPrepaidPlan,
  
  // Features
  getFeaturesForTier,
  getFeaturesForTierAndEnvironment,
  isFeatureEnabled,
  getStorageLimit,
  getDynamicFeatures,
  isDynamicFeatureEnabled,
  RATE_LIMITS,
  
  // Infrastructure
  getInfrastructureConfig,
  getAWSConfig,
  getDomainConfig,
  getStackName,
  getS3BucketName,
  getDynamoTableName,
  getLambdaFunctionName,
  getSecretName,
  
  // Security
  getSecurityConfig,
  getCryptoConfig,
  getJWTConfig,
  getPasswordConfig,
  getSessionConfig,
  getSecurityHeaders,
  validatePassword,
  getSessionTimeout,
  SECURITY_CONSTANTS,
  
  // Payments
  PAYMENT_CONFIG,
  getDisplayPaymentMethods,
  getEnabledPaymentMethods,
  getAllPaymentMethods,
  getPaymentMethod,
  isPaymentMethodEnabled,
  getComingSoonPaymentMethods,

  // Notifications
  getNotificationConfig,
  getSlackConfig,
  isSlackEnabled,
  SLACK_EVENT_TYPES,
  SLACK_MESSAGE_COLORS,
};

// Default export
export default config;